import React from "react";
import { Icon, IconButton, IPickerItemProps, TextField, TooltipHost } from "office-ui-fabric-react";
import { forTimeTrackingFakeField } from "../../../../common/SearchItemsImportPanel";
import { EntityPicker, IFindResult } from "../../../../common/inputs/EntityPickerInput";
import { cancellablePost } from "../../../../../fetch-interceptor";
import { Dictionary, ServerEntityType } from "../../../../../entities/common";
import { ProjectInfo } from "./common";
import "./EditControls.css";

interface ImportItemSearchResult {
    entity: { id: string; attributes: Dictionary<any> };
}

export const FieldLabel = (props: { text: string; tooltip: string; required?: boolean }) => (
    <div className="timesheet-edit-field-label">
        <span>
            {props.text}
            {props.required && <span className="timesheet-edit-required"> *</span>}
        </span>
        <TooltipHost content={props.tooltip}>
            <Icon iconName="Info" className="timesheet-edit-field-info" />
        </TooltipHost>
    </div>
);

export const SourceProjectField = (props: { name: string; tooltip: string }) => (
    <div>
        <FieldLabel text="Source Project" tooltip={props.tooltip} />
        <TextField className="timesheet-edit-project-field" readOnly value={props.name} />
    </div>
);

export const TargetProjectPicker = (props: {
    selected: ProjectInfo | null;
    excludeProjectId?: string;
    fixedOptions?: ProjectInfo[];
    onChange: (project: ProjectInfo | null) => void;
}) => (
    <EntityPicker
        className="timesheet-edit-target-project"
        itemLimit={1}
        selectedItems={props.selected ? [{ id: props.selected.id, name: props.selected.name }] : []}
        onChange={items => props.onChange(items?.[0] ? { id: items[0].id, name: items[0].name } : null)}
        onRenderItem={renderSelectedProject}
        inputProps={{ placeholder: "Search project" }}
        onResolveSuggestions={async (filter: string) => {
            const results = await cancellablePost<ImportItemSearchResult[]>("api/search/importitems", {
                entityType: ServerEntityType.Project,
                attributes: { [forTimeTrackingFakeField.name]: true },
                name: filter
            }).promise;
            return [
                ...(props.fixedOptions ?? []).filter(_ => _.name.toLowerCase().includes(filter.toLowerCase())),
                ...results
                    .filter(_ => _.entity.id !== props.excludeProjectId)
                    .map(_ => ({ id: _.entity.id, name: (_.entity.attributes.Name as string) ?? "" }))
            ];
        }}
    />
);

const renderSelectedProject = (itemProps: IPickerItemProps<IFindResult>) => {
    const { item, onRemoveItem } = itemProps;
    return (
        <div key={item.id} className="timesheet-edit-selected-project" data-is-focusable>
            <span title={item.name}>{item.name}</span>
            <IconButton
                className="timesheet-edit-clear-project"
                onClick={onRemoveItem}
                iconProps={{ iconName: "Cancel", style: { fontSize: "12px" } }}
            />
        </div>
    );
};

export const WarningIcon = (props: { messages: string[] }) =>
    props.messages.length > 0 ? (
        <TooltipHost hostClassName="timesheet-edit-warning" content={props.messages.join(" ")}>
            <Icon iconName="Warning" className="warning-icon" />
        </TooltipHost>
    ) : null;

export const CustomEntryBadge = () => <span className="timesheet-edit-tag tag-custom">Custom</span>;
